import type { InvoiceSettings } from '@clockwise/clients/invoice-settings';
import {
  ApiError,
  DEFAULT_INVOICE_EMAIL_BODY_TEMPLATE,
  DEFAULT_INVOICE_EMAIL_SUBJECT_TEMPLATE,
  DEFAULT_INVOICE_SEND_HOUR,
  DEFAULT_INVOICE_SEND_TIMEZONE,
  findInvoiceEmailSubjectTemplateError,
  findInvoiceEmailTemplateError,
  INVOICE_EMAIL_VARIABLES,
  INVOICE_SENDER_EMAIL_ERROR,
  isInvoiceSenderEmail,
  MAX_INVOICE_EMAIL_BODY_CHARS,
  MAX_INVOICE_EMAIL_SUBJECT_CHARS,
  RETAINER_INVOICE_MODE_LABELS,
  RETAINER_INVOICE_MODES,
  type RetainerInvoiceMode,
  sampleInvoiceEmailValues,
  storedInvoiceEmailTemplate,
  tryRenderInvoiceEmailBody,
  tryRenderInvoiceEmailSubject,
} from '@clockwise/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Braces } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { AccountPicker } from '@/components/custom/account-picker';
import { BackLink } from '@/components/custom/back-link';
import { InvoiceTermsInput, type TermsPreset } from '@/components/custom/invoice-terms-input';
import { NoAccess } from '@/components/custom/no-access';
import { Section } from '@/components/custom/section';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { invoiceSettingsApi } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

export const Route = createFileRoute('/_authenticated/invoices/settings')({
  component: InvoiceSettingsPage,
});

/**
 * Caps mirror `apps/api/.../invoices/settings/validators.ts`, which in turn
 * mirrors the PDF renderer's limits. Duplicated rather than imported because
 * the client schema states them as zod internals, and a form that only learns
 * a line is too long from a 400 makes the operator hunt for which line.
 */
const MAX_ADDRESS_LINES = 6;
const MAX_PAYMENT_LINES = 12;
const MAX_LINE_CHARS = 120;

/**
 * One row of the page: the settings on the left, and what they produce on the
 * right, BESIDE them. A preview pinned to the top of the page would describe a
 * section scrolled out of view; per row, it sits next to the fields it renders
 * and sticks only within that row.
 */
const ROW = 'grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]';

/** The four default-account pickers, in the order a line type is met. */
const DEFAULT_ACCOUNT_FIELDS = [
  {
    key: 'defaultRetainerAccountId',
    label: 'Retainer fee lines',
    help: 'Generated retainer fees.',
  },
  {
    key: 'defaultServiceAccountId',
    label: 'Service lines added by hand',
    help: 'Leave blank so people pick an account each time.',
  },
  {
    key: 'defaultExpenseAccountId',
    label: 'Expense lines',
    help: 'Fixed and variable expenses, generated or added by hand.',
  },
  {
    key: 'defaultDiscountAccountId',
    label: 'Discount lines',
    help: 'SOW discounts and discounts added by hand.',
  },
] as const;

/**
 * The form's shape, which is deliberately NOT the payload's.
 *
 * Two mismatches, both intentional:
 *
 *   - `issuerAddressLines` / `paymentDetails` are `text[]` on the wire and a
 *     single newline-separated textarea here. They are pre-formatted display
 *     lines — typography, not structured data — so the control that matches
 *     them is the one you can paste a letterhead into, not six numbered
 *     inputs.
 *   - Standard terms are the source of truth for due dates; no second setting
 *     is sent or stored.
 */
type FormValues = {
  issuerName: string;
  issuerAddressLines: string;
  issuerPhone: string;
  issuerFax: string;
  issuerEmail: string;
  issuerWebsite: string;
  paymentDetails: string;
  defaultTerms: string;
  defaultCurrency: string;
  retainerInvoiceMode: RetainerInvoiceMode;
  sendHour: string;
  sendTimezone: string;
  /** Always the text on screen — the built-in default when nothing is stored,
   *  so the operator edits a real message rather than a blank box. */
  emailBodyTemplate: string;
  /** Likewise the subject line. */
  emailSubjectTemplate: string;
  /** Default accounts per line type — chart-of-accounts ids, null = none. */
  defaultRetainerAccountId: string | null;
  defaultServiceAccountId: string | null;
  defaultExpenseAccountId: string | null;
  defaultDiscountAccountId: string | null;
};

/** Split a textarea into the `text[]` the API wants: trimmed, blank lines
 *  dropped. Dropping blanks is what stops a trailing newline — which every
 *  paste leaves behind — from 400ing against the server's `min(1)` per line. */
function toLines(value: string): string[] {
  return value
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

/** Reject an over-long or over-full block up front, naming the offending line
 *  number. The server enforces the same caps; this just says *which* line. */
function linesRule(maxLines: number, label: string) {
  return (value: string, ctx: z.RefinementCtx): void => {
    const lines = toLines(value);
    if (lines.length > maxLines) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `At most ${maxLines} ${label} lines (${lines.length} entered).`,
      });
    }
    const tooLong = lines.findIndex((l) => l.length > MAX_LINE_CHARS);
    if (tooLong !== -1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Line ${tooLong + 1} is longer than ${MAX_LINE_CHARS} characters.`,
      });
    }
  };
}

const FormSchema = z.object({
  issuerName: z
    .string()
    .trim()
    .min(1, 'Issuer name is required')
    .max(120, 'Issuer name is too long'),
  issuerAddressLines: z.string().superRefine(linesRule(MAX_ADDRESS_LINES, 'address')),
  issuerPhone: z.string().trim().max(40, 'Phone is too long'),
  issuerFax: z.string().trim().max(40, 'Fax is too long'),
  // Invoices are emailed From this address, so it must be one.
  issuerEmail: z
    .string()
    .trim()
    .max(160, 'Email is too long')
    .refine((s) => s === '' || isInvoiceSenderEmail(s), { message: INVOICE_SENDER_EMAIL_ERROR }),
  issuerWebsite: z.string().trim().max(160, 'Website is too long'),
  paymentDetails: z.string().superRefine(linesRule(MAX_PAYMENT_LINES, 'payment-detail')),
  defaultTerms: z.string().trim().max(60, 'Terms are too long'),
  defaultCurrency: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{3}$/, 'Use a 3-letter ISO-4217 code, e.g. USD.'),
  retainerInvoiceMode: z.enum(RETAINER_INVOICE_MODES),
  sendHour: z
    .string()
    .trim()
    .refine((v) => /^\d{1,2}$/.test(v) && Number(v) <= 23, 'Enter an hour from 0 to 23.'),
  sendTimezone: z
    .string()
    .trim()
    .min(1, 'Required')
    .refine(isValidTimeZone, 'Use an IANA timezone name, e.g. America/Chicago.'),
  emailBodyTemplate: emailTemplateField(
    MAX_INVOICE_EMAIL_BODY_CHARS,
    findInvoiceEmailTemplateError,
  ),
  emailSubjectTemplate: emailTemplateField(
    MAX_INVOICE_EMAIL_SUBJECT_CHARS,
    findInvoiceEmailSubjectTemplateError,
  ),
  // Default accounts per line type — chart-of-accounts ids; null = none.
  defaultRetainerAccountId: z.string().nullable(),
  defaultServiceAccountId: z.string().nullable(),
  defaultExpenseAccountId: z.string().nullable(),
  defaultDiscountAccountId: z.string().nullable(),
});

/** One email template field, checked by the same rule the server applies.
 *  Blank resets to the default, so only a non-blank template is checked. */
function emailTemplateField(max: number, findError: (template: string) => string | null) {
  return z
    .string()
    .max(max, `Up to ${max} characters`)
    .superRefine((v, ctx) => {
      const error = v.trim() === '' ? null : findError(v);
      if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
    });
}

/** Whether the browser knows `timeZone` as an IANA zone — the same test the
 *  server applies, so a bad name is caught under the field, not on save. */
function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    // `Intl` reports an unknown zone only by throwing; that IS the answer.
    return false;
  }
}

/**
 * What an unconfigured tenant's form starts on. These are FORM defaults, not
 * stored ones: nothing is written until Save, so a tenant that opens this
 * screen and leaves still has no settings row and still cannot invoice. The
 * issuer and remittance blocks start empty on purpose — a fabricated address
 * or bank account would print on a real invoice.
 */
const BLANK: FormValues = {
  issuerName: '',
  issuerAddressLines: '',
  issuerPhone: '',
  issuerFax: '',
  issuerEmail: '',
  issuerWebsite: '',
  paymentDetails: '',
  defaultTerms: '',
  defaultCurrency: 'USD',
  retainerInvoiceMode: 'advance',
  sendHour: String(DEFAULT_INVOICE_SEND_HOUR),
  sendTimezone: DEFAULT_INVOICE_SEND_TIMEZONE,
  emailBodyTemplate: DEFAULT_INVOICE_EMAIL_BODY_TEMPLATE,
  emailSubjectTemplate: DEFAULT_INVOICE_EMAIL_SUBJECT_TEMPLATE,
  defaultRetainerAccountId: null,
  defaultServiceAccountId: null,
  defaultExpenseAccountId: null,
  defaultDiscountAccountId: null,
};

function toForm(s: InvoiceSettings): FormValues {
  return {
    issuerName: s.issuerName,
    issuerAddressLines: s.issuerAddressLines.join('\n'),
    issuerPhone: s.issuerPhone ?? '',
    issuerFax: s.issuerFax ?? '',
    issuerEmail: s.issuerEmail ?? '',
    issuerWebsite: s.issuerWebsite ?? '',
    paymentDetails: s.paymentDetails.join('\n'),
    defaultTerms: s.defaultTerms ?? '',
    defaultCurrency: s.defaultCurrency,
    retainerInvoiceMode: s.retainerInvoiceMode,
    sendHour: String(s.sendHour),
    sendTimezone: s.sendTimezone,
    emailBodyTemplate: s.emailBodyTemplate ?? DEFAULT_INVOICE_EMAIL_BODY_TEMPLATE,
    emailSubjectTemplate: s.emailSubjectTemplate ?? DEFAULT_INVOICE_EMAIL_SUBJECT_TEMPLATE,
    defaultRetainerAccountId: s.defaultRetainerAccountId,
    defaultServiceAccountId: s.defaultServiceAccountId,
    defaultExpenseAccountId: s.defaultExpenseAccountId,
    defaultDiscountAccountId: s.defaultDiscountAccountId,
  };
}

/** Empty optional text is sent as `null`, never `''`: the columns are nullable
 *  and a stored empty string renders as a labelled-but-blank row in the issuer
 *  block, which reads to the customer as a data bug. */
const orNull = (v: string): string | null => (v.trim().length === 0 ? null : v.trim());

function toInput(v: FormValues) {
  return {
    issuerName: v.issuerName.trim(),
    issuerAddressLines: toLines(v.issuerAddressLines),
    issuerPhone: orNull(v.issuerPhone),
    issuerFax: orNull(v.issuerFax),
    issuerEmail: orNull(v.issuerEmail),
    issuerWebsite: orNull(v.issuerWebsite),
    paymentDetails: toLines(v.paymentDetails),
    defaultTerms: orNull(v.defaultTerms),
    defaultCurrency: v.defaultCurrency.trim().toUpperCase(),
    retainerInvoiceMode: v.retainerInvoiceMode,
    sendHour: Number(v.sendHour),
    sendTimezone: v.sendTimezone.trim(),
    // Null — "use the built-in default" — when blank or unchanged from it, so
    // only a tenant that actually customises the email stores a template.
    emailBodyTemplate: storedInvoiceEmailTemplate(
      v.emailBodyTemplate,
      DEFAULT_INVOICE_EMAIL_BODY_TEMPLATE,
    ),
    emailSubjectTemplate: storedInvoiceEmailTemplate(
      v.emailSubjectTemplate,
      DEFAULT_INVOICE_EMAIL_SUBJECT_TEMPLATE,
    ),
    defaultRetainerAccountId: v.defaultRetainerAccountId,
    defaultServiceAccountId: v.defaultServiceAccountId,
    defaultExpenseAccountId: v.defaultExpenseAccountId,
    defaultDiscountAccountId: v.defaultDiscountAccountId,
  };
}

const FORM_FIELDS = Object.keys(BLANK) as (keyof FormValues)[];
function isFormField(s: string): s is keyof FormValues {
  return (FORM_FIELDS as string[]).includes(s);
}

/**
 * Map a server field error back onto a form control.
 *
 * Array-element paths arrive segmented — `paymentDetails.3` — because the
 * payload is an array where the form is one textarea. Fold the index into the
 * message so the error still lands on the right control AND still says which
 * line, rather than being dropped for not matching a field name.
 */
function applyServerFieldError(
  form: ReturnType<typeof useForm<FormValues>>,
  path: string,
  message: string,
): boolean {
  const [head, index] = path.split('.');
  if (head === undefined || !isFormField(head)) return false;
  const prefixed =
    index !== undefined && /^\d+$/.test(index) ? `Line ${Number(index) + 1}: ${message}` : message;
  form.setError(head, { message: prefixed });
  return true;
}

function InvoiceSettingsPage() {
  const canView = useHasPermission('invoice.settings.view');
  const canEdit = useHasPermission('invoice.settings.edit');
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: queryKeys.invoiceSettings.current(),
    queryFn: () => invoiceSettingsApi.get(),
    enabled: canView,
  });
  // `null` is a first-class state, not an error: the tenant has never saved.
  const settings: InvoiceSettings | null = settingsQuery.data?.data ?? null;
  const configured = settings !== null;

  const form = useForm<FormValues>({ resolver: zodResolver(FormSchema), defaultValues: BLANK });

  /** Whether the saved row has already been folded into the form. Guards the
   *  effect below against re-seeding on a later refetch. */
  const seeded = useRef(false);

  // Seed ONCE the row arrives, and never again.
  //
  // Skipped when there is no row — the form is already sitting on BLANK, and
  // resetting to it would clobber anything typed while the request was in
  // flight. The `seeded` latch covers the harder case: this effect keys on the
  // `settings` object identity, so a refetch that returns *changed* data (a
  // second admin saved the singleton, then this tab refetched on remount or
  // reconnect) would otherwise `reset` every field out from under an operator
  // mid-way through transcribing a bank account — silently, and clearing
  // `isDirty` so Save greys out too. Post-save re-seeding is handled
  // explicitly by the mutation's own `form.reset`.
  useEffect(() => {
    if (!settings || seeded.current) return;
    seeded.current = true;
    form.reset(toForm(settings));
  }, [settings, form]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => invoiceSettingsApi.update(toInput(values)),
    onSuccess: (res) => {
      toast.success('Invoice settings saved');
      queryClient.setQueryData(queryKeys.invoiceSettings.current(), res);
      // The issuer block, remittance lines and defaults are cached under
      // `invoices.newDefaults`. Without this, the create screen keeps
      // serving the pre-save copy — or keeps serving the not-configured
      // error that brought the operator here.
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      form.reset(toForm(res.data));
    },
    onError: (err) => {
      if (err instanceof ApiError && err.fields?.length) {
        const unbound: string[] = [];
        for (const fe of err.fields) {
          if (!applyServerFieldError(form, fe.path, fe.message)) unbound.push(fe.message);
        }
        toast.error(unbound.length > 0 ? unbound.join(' ') : 'Please fix the highlighted fields');
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
  });

  if (!canView) {
    return <NoAccess permission="invoice.settings.view" resource="Invoice settings" />;
  }

  const errors = form.formState.errors;
  const values = form.watch();
  const onSubmit = form.handleSubmit((v) => mutation.mutate(v));
  const pickDefaultTerms = (preset: TermsPreset) => {
    const edit = { shouldDirty: true, shouldValidate: true } as const;
    form.setValue('defaultTerms', preset.label, edit);
  };

  return (
    <PageShell className="gap-6 overflow-y-auto">
      <div>
        <BackLink to="/invoices">Back to Invoices</BackLink>
      </div>

      <PageHeader
        title="Invoice settings"
        subtitle="Who the invoice is from, where the money goes, and the defaults every new invoice inherits."
      />

      {settingsQuery.isLoading ? (
        <div className="max-w-2xl space-y-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : settingsQuery.isError ? (
        <p className="text-sm text-destructive">
          {settingsQuery.error instanceof ApiError
            ? settingsQuery.error.message
            : 'Failed to load invoice settings.'}
        </p>
      ) : (
        // noValidate: the schema validates, and says why inline under the
        // field — the browser's own check on type="email" would block the
        // submit with a popup instead.
        <form noValidate onSubmit={onSubmit} className="flex flex-col gap-6">
          <div className={ROW}>
            <div className="flex min-w-0 flex-col gap-6">
              {!configured ? (
                // G15 — a data gap must not look like normal output. Until this
                // is saved, `requireForInvoicing` refuses and no invoice can be
                // raised at all, so say that rather than letting a blank form
                // read as "nothing to do here".
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                  <p className="font-medium">Invoicing is not set up yet.</p>
                  <p className="mt-0.5 text-muted-foreground text-xs">
                    No invoice can be raised until these are saved. Nothing is stored until you
                    press Save.
                  </p>
                </div>
              ) : null}

              <Section
                title="Issuer"
                description="The “from” block printed top-left on every page of the invoice."
              >
                <div className="space-y-1.5">
                  <Label htmlFor="issuerName">Issuer name</Label>
                  <Input
                    id="issuerName"
                    disabled={!canEdit}
                    aria-invalid={Boolean(errors.issuerName)}
                    {...form.register('issuerName')}
                  />
                  <FieldError message={errors.issuerName?.message} />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="issuerAddressLines">Address</Label>
                  <Textarea
                    id="issuerAddressLines"
                    rows={4}
                    disabled={!canEdit}
                    placeholder={
                      '123 Market Street, Suite 400\nSan Francisco, CA 94105\nUnited States'
                    }
                    aria-invalid={Boolean(errors.issuerAddressLines)}
                    {...form.register('issuerAddressLines')}
                  />
                  <p className="text-muted-foreground text-xs">
                    One line per line of the address, printed exactly as typed — up to{' '}
                    {MAX_ADDRESS_LINES} lines. The invoice never re-orders them, so international
                    formats work as written.
                  </p>
                  <FieldError message={errors.issuerAddressLines?.message} />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="issuerPhone">Phone</Label>
                    <Input
                      id="issuerPhone"
                      disabled={!canEdit}
                      aria-invalid={Boolean(errors.issuerPhone)}
                      {...form.register('issuerPhone')}
                    />
                    <FieldError message={errors.issuerPhone?.message} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="issuerFax">Fax</Label>
                    <Input
                      id="issuerFax"
                      disabled={!canEdit}
                      aria-invalid={Boolean(errors.issuerFax)}
                      {...form.register('issuerFax')}
                    />
                    <FieldError message={errors.issuerFax?.message} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="issuerEmail">Email</Label>
                    <Input
                      id="issuerEmail"
                      type="email"
                      disabled={!canEdit}
                      aria-invalid={Boolean(errors.issuerEmail)}
                      {...form.register('issuerEmail')}
                    />
                    <FieldError message={errors.issuerEmail?.message} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="issuerWebsite">Website</Label>
                    <Input
                      id="issuerWebsite"
                      disabled={!canEdit}
                      aria-invalid={Boolean(errors.issuerWebsite)}
                      {...form.register('issuerWebsite')}
                    />
                    <FieldError message={errors.issuerWebsite?.message} />
                  </div>
                </div>
                <p className="text-muted-foreground text-xs">
                  Phone, fax, email and website are optional; a blank one is omitted from the
                  invoice rather than printed empty. Invoices are emailed to customers from the
                  Email address, so it can’t be left blank if you send invoices from here.
                </p>
              </Section>

              <Section
                title="Payment instructions"
                description="Printed verbatim in the remittance box at the bottom of the last page."
              >
                <div className="space-y-1.5">
                  <Label htmlFor="paymentDetails">Remittance lines</Label>
                  <Textarea
                    id="paymentDetails"
                    rows={6}
                    disabled={!canEdit}
                    placeholder={
                      'Bank: First National Bank\nAccount name: Acme LLC\nAccount number: 1234567890\nRouting (ABA): 021000021\nZelle: ar@acme.com'
                    }
                    aria-invalid={Boolean(errors.paymentDetails)}
                    {...form.register('paymentDetails')}
                  />
                  <p className="text-muted-foreground text-xs">
                    One line each, up to {MAX_PAYMENT_LINES}. This is where your customers send
                    money — check it against your bank before saving.
                  </p>
                  <FieldError message={errors.paymentDetails?.message} />
                </div>
              </Section>
            </div>
            <IssuerPreview values={values} />
          </div>

          <div className={ROW}>
            <Section
              title="Invoice defaults"
              description="Copied onto each new invoice when it is created. Changing them here never rewrites an invoice that already exists."
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="defaultTerms">Terms</Label>
                  <InvoiceTermsInput
                    id="defaultTerms"
                    disabled={!canEdit}
                    invalid={Boolean(errors.defaultTerms)}
                    onPick={pickDefaultTerms}
                    inputProps={form.register('defaultTerms')}
                  />
                  <p className="text-muted-foreground text-xs">
                    The selected terms determine the due date on each new invoice.
                  </p>
                  <FieldError message={errors.defaultTerms?.message} />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="sendHour">Send time (hour)</Label>
                  <Input
                    id="sendHour"
                    inputMode="numeric"
                    placeholder="9"
                    disabled={!canEdit}
                    aria-invalid={Boolean(errors.sendHour)}
                    {...form.register('sendHour')}
                  />
                  <p className="text-muted-foreground text-xs">
                    Hour of the day (0–23) scheduled invoices are emailed, in the timezone below.
                  </p>
                  <FieldError message={errors.sendHour?.message} />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="sendTimezone">Send timezone</Label>
                  <Input
                    id="sendTimezone"
                    placeholder="America/Chicago"
                    disabled={!canEdit}
                    aria-invalid={Boolean(errors.sendTimezone)}
                    {...form.register('sendTimezone')}
                  />
                  <p className="text-muted-foreground text-xs">
                    IANA name, e.g. America/Chicago. Decides when a send date starts.
                  </p>
                  <FieldError message={errors.sendTimezone?.message} />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="defaultCurrency">Currency</Label>
                  <Input
                    id="defaultCurrency"
                    maxLength={3}
                    className="uppercase"
                    disabled={!canEdit}
                    aria-invalid={Boolean(errors.defaultCurrency)}
                    {...form.register('defaultCurrency')}
                  />
                  <p className="text-muted-foreground text-xs">3-letter ISO-4217 code.</p>
                  <FieldError message={errors.defaultCurrency?.message} />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="retainerInvoiceMode">Retainer invoicing</Label>
                  <Select
                    // Radix Select captures its value at mount and won't cleanly
                    // adopt one applied later (the post-load reset), leaving the
                    // trigger blank. Key on the value so hydration remounts it
                    // directly onto the saved mode.
                    key={values.retainerInvoiceMode}
                    value={values.retainerInvoiceMode}
                    disabled={!canEdit}
                    onValueChange={(v) =>
                      form.setValue('retainerInvoiceMode', v as RetainerInvoiceMode, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                  >
                    <SelectTrigger id="retainerInvoiceMode" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RETAINER_INVOICE_MODES.map((m) => (
                        <SelectItem key={m} value={m}>
                          {RETAINER_INVOICE_MODE_LABELS[m]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground text-xs">
                    Time &amp; materials is always billed in arrears and ignores this.
                  </p>
                  <FieldError message={errors.retainerInvoiceMode?.message} />
                </div>
              </div>
            </Section>
          </div>

          <EmailBodySection form={form} canEdit={canEdit} />

          <div className={ROW}>
            <Section
              title="Default accounts"
              description="The account each new or generated line starts with. Lines of a type with no default start empty, and someone picks an account before the invoice can be approved. Changing a default affects new lines only."
            >
              <div className="grid gap-4 sm:grid-cols-2">
                {DEFAULT_ACCOUNT_FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label htmlFor={f.key}>{f.label}</Label>
                    <AccountPicker
                      id={f.key}
                      value={values[f.key]}
                      disabled={!canEdit}
                      hasError={Boolean(errors[f.key])}
                      placeholder="No default"
                      onChange={(id) =>
                        form.setValue(f.key, id, { shouldDirty: true, shouldValidate: true })
                      }
                    />
                    <p className="text-muted-foreground text-xs">{f.help}</p>
                    <FieldError message={errors[f.key]?.message} />
                  </div>
                ))}
              </div>
              <p className="mt-3 text-muted-foreground text-xs">
                T&amp;M lines take the T&amp;M account of the activities their hours were logged
                against, so they need no default here.
              </p>
            </Section>
          </div>

          <div className={ROW}>
            <Section
              title="Invoice numbering"
              description="Numbers read {yy}-{mm}-{nnnn} — the year and month the invoice is raised, then a four-digit count that restarts at 0001 each month."
            >
              {/* No control here: numbering is not configurable. Every month
                  starts at 0001, so there is no seed to set and nothing an
                  operator can get wrong. The preview is the whole section. */}
              {settings ? (
                <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                  Next invoice number:{' '}
                  <span className="num-code">{settings.nextInvoiceNumberPreview}</span>
                  <p className="mt-0.5 text-muted-foreground text-xs">
                    A preview, not a reservation — raised in a later month, the date prefix moves
                    and the count restarts at 0001.
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  This tenant&rsquo;s first invoice will be numbered{' '}
                  <span className="num-code">
                    {String(new Date().getUTCFullYear() % 100).padStart(2, '0')}-
                    {String(new Date().getUTCMonth() + 1).padStart(2, '0')}-0001
                  </span>{' '}
                  if raised this month.
                </p>
              )}
            </Section>
          </div>

          {canEdit ? (
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={mutation.isPending || !form.formState.isDirty}>
                {mutation.isPending
                  ? 'Saving…'
                  : configured
                    ? 'Save settings'
                    : 'Save and enable invoicing'}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={mutation.isPending || !form.formState.isDirty}
                onClick={() => form.reset(settings ? toForm(settings) : BLANK)}
              >
                Discard changes
              </Button>
              {settings?.updatedAt ? (
                <span className="text-muted-foreground text-xs">
                  Last saved {new Date(settings.updatedAt).toLocaleString()}
                </span>
              ) : null}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">
              You can view these settings but not change them. `invoice.settings.edit` is needed to
              save.
            </p>
          )}
        </form>
      )}
    </PageShell>
  );
}

/**
 * The email every invoice is sent with, as a template. An invoice can replace
 * the message for itself in its Delivery section; this is what all the others
 * say, read at the moment each one is sent.
 */
function EmailBodySection({
  form,
  canEdit,
}: {
  form: ReturnType<typeof useForm<FormValues>>;
  canEdit: boolean;
}) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const subjectRef = useRef<HTMLInputElement | null>(null);
  /** Which field "Insert variable" writes into: the one last focused. */
  const targetRef = useRef<'emailSubjectTemplate' | 'emailBodyTemplate'>('emailBodyTemplate');
  const template = form.watch('emailBodyTemplate');
  const subjectTemplate = form.watch('emailSubjectTemplate');
  const issuerName = form.watch('issuerName');
  const error = form.formState.errors.emailBodyTemplate?.message;
  const subjectError = form.formState.errors.emailSubjectTemplate?.message;
  const { ref, ...registered } = form.register('emailBodyTemplate');
  const { ref: subjectRegisterRef, ...subjectRegistered } = form.register('emailSubjectTemplate');
  const edit = { shouldDirty: true, shouldValidate: true } as const;

  const sample = sampleInvoiceEmailValues(issuerName);
  const preview =
    template.trim() === ''
      ? tryRenderInvoiceEmailBody(DEFAULT_INVOICE_EMAIL_BODY_TEMPLATE, sample)
      : tryRenderInvoiceEmailBody(template, sample);
  const subjectPreview = tryRenderInvoiceEmailSubject(
    subjectTemplate.trim() === '' ? DEFAULT_INVOICE_EMAIL_SUBJECT_TEMPLATE : subjectTemplate,
    sample,
  );
  const customised =
    template.trim() !== DEFAULT_INVOICE_EMAIL_BODY_TEMPLATE ||
    subjectTemplate.trim() !== DEFAULT_INVOICE_EMAIL_SUBJECT_TEMPLATE;

  /** Put `{{name}}` where the caret is in the last-focused field, replacing
   *  any selection. */
  const insertVariable = (name: string) => {
    const field = targetRef.current;
    const el = field === 'emailSubjectTemplate' ? subjectRef.current : textareaRef.current;
    const value = field === 'emailSubjectTemplate' ? subjectTemplate : template;
    const token = `{{${name}}}`;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    form.setValue(field, value.slice(0, start) + token + value.slice(end), edit);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  return (
    <div className={ROW}>
      <Section
        title="Invoice email"
        description="The subject and message every invoice is emailed with, filled in when it is sent. An invoice can use its own message instead, from its Delivery section."
      >
        <div className="space-y-1.5">
          <Label htmlFor="emailSubjectTemplate">Subject</Label>
          <Input
            id="emailSubjectTemplate"
            disabled={!canEdit}
            aria-invalid={Boolean(subjectError)}
            className="font-mono text-xs"
            {...subjectRegistered}
            onFocus={() => {
              targetRef.current = 'emailSubjectTemplate';
            }}
            ref={(el) => {
              subjectRegisterRef(el);
              subjectRef.current = el;
            }}
          />
          <FieldError message={subjectError} />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-end justify-between gap-2">
            <Label htmlFor="emailBodyTemplate">Email body</Label>
            {canEdit ? (
              <div className="flex items-center gap-3">
                {customised ? (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    onClick={() => {
                      form.setValue('emailBodyTemplate', DEFAULT_INVOICE_EMAIL_BODY_TEMPLATE, edit);
                      form.setValue(
                        'emailSubjectTemplate',
                        DEFAULT_INVOICE_EMAIL_SUBJECT_TEMPLATE,
                        edit,
                      );
                    }}
                  >
                    Reset to default
                  </Button>
                ) : null}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" size="sm" className="h-7 gap-1 text-xs">
                      <Braces className="size-3.5" />
                      Insert variable
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-72"
                    // Focus goes back to the text, at the inserted variable —
                    // not to the trigger, which is Radix's default.
                    onCloseAutoFocus={(e) => e.preventDefault()}
                  >
                    <DropdownMenuLabel className="text-muted-foreground text-xs">
                      Inserted at the cursor, in the subject or body
                    </DropdownMenuLabel>
                    {INVOICE_EMAIL_VARIABLES.map((v) => (
                      <DropdownMenuItem
                        key={v.name}
                        onSelect={() => insertVariable(v.name)}
                        data-multiline
                        className="flex flex-col items-start gap-0.5"
                      >
                        <span className="num-code text-xs">{`{{${v.name}}}`}</span>
                        <span className="text-muted-foreground text-xs">
                          {v.label} · {v.name === 'issuerName' ? sample.issuerName : v.example}
                        </span>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <p className="px-2 py-1.5 text-muted-foreground text-xs">
                      Wrap a sentence in{' '}
                      <code className="num-code">{'{{#if dueDate}}…{{/if}}'}</code> to leave it out
                      when the invoice has no value for it.
                    </p>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : null}
          </div>
          <Textarea
            id="emailBodyTemplate"
            rows={10}
            disabled={!canEdit}
            aria-invalid={Boolean(error)}
            aria-describedby="emailBodyTemplate-help"
            className="font-mono text-xs"
            {...registered}
            onFocus={() => {
              targetRef.current = 'emailBodyTemplate';
            }}
            ref={(el) => {
              ref(el);
              textareaRef.current = el;
            }}
          />
          <FieldError message={error} />
          <p id="emailBodyTemplate-help" className="text-muted-foreground text-xs">
            Plain text; a blank line starts a new paragraph. The PDF is always attached.
          </p>
        </div>
      </Section>

      <div className="lg:sticky lg:top-4 lg:self-start">
        <Section title="Preview" description="The email, filled in with sample values.">
          <section
            className="rounded-md border bg-white p-4 text-neutral-900 text-sm"
            aria-label="Email preview"
          >
            <p className="mb-3 border-b pb-2 text-neutral-500 text-xs">
              Subject: {subjectPreview ?? <span className="italic">fix the subject to see it</span>}
            </p>
            {preview === null ? (
              <p className="text-neutral-400 italic">Fix the template to see the preview.</p>
            ) : (
              <div className="space-y-3 whitespace-pre-line">
                {preview.split(/\n\s*\n/).map((p, i) => (
                  // Paragraphs of free text may repeat; position is their identity.
                  <p key={i}>{p}</p>
                ))}
              </div>
            )}
          </section>
        </Section>
      </div>
    </div>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-destructive text-xs">{message}</p>;
}

/**
 * The two typography blocks as the invoice will print them.
 *
 * Worth the space because both fields are free-form display lines: the only
 * question an operator actually has is "what will this look like on the
 * page?", and the alternative — raise a draft invoice and render a PDF to find
 * out — burns an invoice number.
 */
function IssuerPreview({ values }: { values: FormValues }) {
  // Keyed by index throughout: these are positional display lines that may
  // legitimately repeat, so the position is the only identity they have.
  const address = toLines(values.issuerAddressLines);
  const payment = toLines(values.paymentDetails);
  const contact = [values.issuerPhone, values.issuerFax, values.issuerEmail, values.issuerWebsite]
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

  return (
    <div className="lg:sticky lg:top-4 lg:self-start">
      <Section title="Preview" description="How these blocks print on the invoice.">
        <div className="space-y-4 rounded-md border bg-white p-4 text-neutral-900">
          <div>
            <p className="mb-1 font-semibold text-[10px] text-neutral-400 uppercase tracking-wide">
              From
            </p>
            {values.issuerName.trim() ? (
              <p className="font-semibold text-sm">{values.issuerName.trim()}</p>
            ) : (
              <p className="text-neutral-400 text-sm italic">Issuer name</p>
            )}
            {address.map((line, i) => (
              // Address lines are display text and may legitimately repeat
              // (two "Suite 400"s in different blocks), so the index is the
              // only stable identity available here.
              <p key={i} className="text-sm">
                {line}
              </p>
            ))}
            {contact.map((line, i) => (
              <p key={i} className="text-neutral-600 text-xs">
                {line}
              </p>
            ))}
          </div>

          <div className="border-t pt-3">
            <p className="mb-1 font-semibold text-[10px] text-neutral-400 uppercase tracking-wide">
              Remit to
            </p>
            {payment.length > 0 ? (
              payment.map((line, i) => (
                <p key={i} className="text-sm">
                  {line}
                </p>
              ))
            ) : (
              <p className="text-neutral-400 text-sm italic">
                No payment instructions — the invoice would not say where to send money.
              </p>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}
